import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { downloadCsv } from "@/lib/api";
import { fmtDateTime } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollText, Search, Download } from "lucide-react";

const ACTION_COLOR = {
  ingested: "text-blue-400", promoted: "text-emerald-400", rejected: "text-rose-400",
  score_override: "text-violet-400", draft_generated: "text-primary", draft_edited: "text-amber-400",
  outreach_approved: "text-emerald-400", submission_approved: "text-emerald-400",
  outreach_sent: "text-primary", submission_completed: "text-teal-400", rescored: "text-violet-400",
};

export default function AuditLog() {
  const [q, setQ] = useState("");
  const { data: logs = [], isLoading } = useQuery({ queryKey: ["audit-all"], queryFn: async () => (await api.get("/activity?limit=500")).data });

  const filtered = logs.filter((l) => !q || `${l.action} ${l.detail} ${l.actor}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto space-y-6" data-testid="audit-view">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Audit Trail</h1>
          <p className="text-sm text-muted-foreground mt-1">Immutable record: ingestion, scoring, AI drafts, edits, approvals and sends.</p>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search audit log…" data-testid="audit-search" className="pl-9" />
          </div>
          <Button variant="outline" className="gap-2 shrink-0" data-testid="export-audit-btn"
            onClick={async () => { try { await downloadCsv("/export/audit.csv", "cadence_audit_trail.csv"); toast.success("Audit CSV downloaded"); } catch { toast.error("Export failed"); } }}>
            <Download className="h-4 w-4" /> Export CSV
          </Button>
        </div>
      </header>

      <Card className="border-border overflow-hidden">
        {isLoading ? <p className="text-sm text-muted-foreground p-8 text-center">Loading…</p> : (
          <ol className="divide-y divide-border">
            {filtered.map((l) => (
              <li key={l.id} className="flex items-start gap-3 p-4 hover:bg-accent/40 transition-colors" data-testid={`audit-row-${l.id}`}>
                <ScrollText className={`h-4 w-4 mt-0.5 shrink-0 ${ACTION_COLOR[l.action] || "text-muted-foreground"}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium capitalize">{l.action.replace(/_/g, " ")}</span>
                    <Badge variant="outline" className="text-[10px]">{l.entity_type}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5">{l.detail}</p>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs text-muted-foreground">{fmtDateTime(l.timestamp)}</div>
                  <div className="text-[11px] text-muted-foreground/70">{l.actor}</div>
                </div>
              </li>
            ))}
            {filtered.length === 0 && <li className="p-12 text-center text-sm text-muted-foreground">No matching audit entries.</li>}
          </ol>
        )}
      </Card>
    </div>
  );
}
